import type { DirectMpBlockedReason } from "@/features/hoverIdle/mpm/directMpBlockState";

export type DirectMpTargetVisualState =
  | "normal"
  | "hoverLike"
  | "blocked"
  | "locked"
  | "disabled";

export interface DirectMpTargetViewState {
  readonly assigned: boolean;
  readonly effective: boolean;
  readonly operative: boolean;
  readonly mpBlocked: boolean;
  readonly mpBlockedReason?: DirectMpBlockedReason;
  readonly showSlot: boolean;
  readonly canAssign: boolean;
  readonly canRemove: boolean;
  readonly blockManualPointer: boolean;
  readonly showPauseOverlay: boolean;
  readonly showBlockedOverlay: boolean;
  readonly directMpBlockedByActiveWall: boolean;
  readonly directMpBlockedReason?: DirectMpBlockedReason;
  readonly visualState: DirectMpTargetVisualState;
}

export interface DirectMpTargetViewStateInput {
  readonly assigned: boolean;
  readonly targetActive: boolean;
  readonly sectionEnabled?: boolean;
  readonly canAssign: boolean;
  readonly canRemove?: boolean;
  readonly blockManualPointerWhenAssigned?: boolean;
  readonly directMpBlockedByActiveWall?: boolean;
  readonly directMpBlockedReason?: DirectMpBlockedReason;
  readonly targetDisabled?: boolean;
  readonly visualWhenInactive?: Extract<
    DirectMpTargetVisualState,
    "locked" | "disabled"
  >;
}

export function resolveDirectMpTargetViewState(
  input: DirectMpTargetViewStateInput,
): DirectMpTargetViewState {
  const assigned = input.assigned;
  const targetActive = input.targetActive;
  const sectionEnabled = input.sectionEnabled ?? true;
  const targetDisabled = input.targetDisabled ?? false;
  const directMpBlockedByActiveWall =
    input.directMpBlockedByActiveWall === true;
  const canAssign =
    !assigned &&
    sectionEnabled &&
    targetActive &&
    !targetDisabled &&
    !directMpBlockedByActiveWall &&
    input.canAssign;
  const canRemove = assigned && (input.canRemove ?? true);
  const effective =
    assigned &&
    sectionEnabled &&
    targetActive &&
    !targetDisabled &&
    !directMpBlockedByActiveWall;
  const operative = effective;
  const blockManualPointer =
    operative && (input.blockManualPointerWhenAssigned ?? false);
  const showPauseOverlay = assigned && !sectionEnabled;
  const showBlockedOverlay =
    assigned && sectionEnabled && directMpBlockedByActiveWall;
  const mpBlocked = showBlockedOverlay;

  return {
    assigned,
    effective,
    operative,
    mpBlocked,
    ...(mpBlocked && input.directMpBlockedReason !== undefined
      ? { mpBlockedReason: input.directMpBlockedReason }
      : {}),
    showSlot: canAssign || canRemove,
    canAssign,
    canRemove,
    blockManualPointer,
    showPauseOverlay,
    showBlockedOverlay,
    directMpBlockedByActiveWall,
    ...(directMpBlockedByActiveWall && input.directMpBlockedReason !== undefined
      ? { directMpBlockedReason: input.directMpBlockedReason }
      : {}),
    visualState: resolveDirectMpTargetVisualStateKind({
      assigned,
      sectionEnabled,
      targetActive,
      targetDisabled,
      directMpBlockedByActiveWall,
      visualWhenInactive: input.visualWhenInactive ?? "disabled",
    }),
  };
}

function resolveDirectMpTargetVisualStateKind(options: {
  readonly assigned: boolean;
  readonly sectionEnabled: boolean;
  readonly targetActive: boolean;
  readonly targetDisabled: boolean;
  readonly directMpBlockedByActiveWall: boolean;
  readonly visualWhenInactive: Extract<
    DirectMpTargetVisualState,
    "locked" | "disabled"
  >;
}): DirectMpTargetVisualState {
  if (
    options.assigned &&
    options.sectionEnabled &&
    options.directMpBlockedByActiveWall
  ) {
    return "blocked";
  }
  if (!options.targetActive) {
    return options.visualWhenInactive;
  }
  if (options.targetDisabled) {
    return "disabled";
  }
  if (options.assigned && options.sectionEnabled) {
    return "hoverLike";
  }
  return "normal";
}
